import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ExternalLink, Rocket, Clock, Zap, Phone } from 'lucide-react';
import { DEMO_SITES, OFFER } from '../constants/demos';

const Examples = () => {
  return (
    <div className="min-h-screen pt-16 pb-24 px-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center mb-14 pt-10">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-5 py-2 mb-6">
            <Zap className="w-4 h-4 text-blue-400" />
            <span className="text-blue-300 text-sm font-semibold">
              Each site: generated + deployed in under an hour
            </span>
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">Real Examples, Live on the Web</h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            These six sites were built by our AI team — same pipeline we use for customer orders —
            and each one went from a blank page to a live, deployed website in under an hour.
            Click any card to explore the real thing.
          </p>
          <p className="text-gray-500 text-sm mt-4">
            All business names are fictional demo brands. Your site gets the same treatment —
            plus a human review round and your real content.
          </p>
        </div>

        {/* Demo grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {DEMO_SITES.map((demo) => (
            <Card
              key={demo.slug}
              className="bg-gray-800/50 border-gray-700 overflow-hidden hover:border-blue-500/60 transition-all duration-300 hover:scale-[1.02] group flex flex-col"
            >
              <a href={demo.url} target="_blank" rel="noopener noreferrer" className="block">
                <div className="relative h-56 overflow-hidden bg-gray-900">
                  <img
                    src={demo.img}
                    alt={`${demo.industry} website example — ${demo.business}`}
                    loading="lazy"
                    className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 transition-all duration-500 group-hover:object-bottom"
                  />
                </div>
              </a>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-blue-400">
                    {demo.industry}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-green-400">
                    <Clock className="w-3 h-3" />
                    under 1 hour
                  </span>
                </div>
                <h2 className="text-white text-lg font-bold mb-3">{demo.business}</h2>
                <div className="mt-auto">
                  <a href={demo.url} target="_blank" rel="noopener noreferrer">
                    <Button
                      variant="outline"
                      className="w-full border-gray-600 text-gray-200 hover:bg-blue-500/10 hover:border-blue-500/60"
                    >
                      Visit Live Site
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Honest process note */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Card className="bg-gray-800/40 border-gray-700 p-6">
            <p className="text-gray-300 leading-relaxed">
              <span className="text-white font-semibold">How these were made:</span> our AI pipeline
              generated each site's design, copy, and layout, then deployed it straight to live
              hosting — generated and deployed in under an hour each. For paying customers we add
              what demos don't get: your real business details, a human quality review of every page,
              and one full revision round — all inside the 48-hour window.
            </p>
          </Card>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Want yours next to these?</h2>
          <p className="text-gray-400 mb-8">
            ${OFFER.price} one-time, founding rate for the first {OFFER.foundingSpots} orders (then ${OFFER.anchorPrice}). Live in 48 hours.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/order">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-green-500/30">
                <Rocket className="w-5 h-5 mr-2" />
                Start My Website — ${OFFER.price}
              </Button>
            </Link>
            <a href={`tel:${OFFER.phoneTel}`}>
              <Button size="lg" variant="outline" className="border-2 border-blue-500 text-blue-400 hover:bg-blue-500/10 px-8 py-6 text-lg rounded-xl">
                <Phone className="w-5 h-5 mr-2" />
                Call {OFFER.phoneDisplay}
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Examples;
